/**
 * Tiny helpers shared by every component in this package.
 *
 * No `clsx`/`tailwind-merge` dependency: the components in here never fight
 * over conflicting Tailwind classes because each accepts a single `className`
 * that is appended LAST, so the caller always wins.
 */

export type ClassValue = string | number | false | null | undefined;

/** Join truthy class fragments. */
export function cn(...values: ClassValue[]): string {
  let out = '';
  for (const v of values) {
    if (v === false || v === null || v === undefined || v === '' || v === 0) continue;
    out = out === '' ? String(v) : `${out} ${String(v)}`;
  }
  return out;
}

/**
 * The focus ring used by EVERY interactive element in the system.
 *
 * Accessibility intent: keyboard users must always see where they are.
 * `focus-visible` (not `focus`) so a mouse click does not leave a ring behind,
 * and the ring is offset so it stays visible on dark rows too.
 */
export const FOCUS_RING =
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

/** Disabled styling, identical everywhere so "not allowed" is never ambiguous. */
export const DISABLED = 'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

let idCounter = 0;

/**
 * Stable-enough id for `aria-describedby` / `htmlFor` wiring.
 * React 18's `useId` is preferred inside components; this exists for the few
 * places that need an id outside a render.
 */
export function nextId(prefix = 'sa'): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}
